import type { ModalActions } from "@/hooks/useModal";
import type { ProjectSummary } from "@/hooks/useProjectBoardData";
import { useRecentlyChangedRowsApi } from "@/contexts/RecentlyChangedRowsContext";
import { db } from "@/lib/supabase";
import { fullRowChanges, recordAudit } from "@/lib/audit";
import { useState } from "react";

interface ConfirmHardDeleteProjectModalProps extends ModalActions {
    project: ProjectSummary;
    onConfirm: () => void;
}

export function ConfirmHardDeleteProjectModal({
    project,
    onConfirm,
    close,
}: ConfirmHardDeleteProjectModalProps) {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { markSelfEdit } = useRecentlyChangedRowsApi();

    const handleConfirm = async () => {
        if (submitting) return;

        setError(null);
        setSubmitting(true);

        try {
            // Delete assignments first — don't rely on FK cascade
            const { data: assignments, error: fetchError } = await db.projectAssignments.getByProject(project.id);
            if (fetchError) {
                setError("Failed to fetch project assignments");
                setSubmitting(false);
                return;
            }

            for (const assignment of assignments ?? []) {
                markSelfEdit("project_assignments", assignment.id);
                const { error: deleteAssignmentError } = await db.projectAssignments.delete(assignment.id);
                if (deleteAssignmentError) {
                    setError("Failed to delete project assignment");
                    setSubmitting(false);
                    return;
                }
                recordAudit({
                    table: "project_assignments",
                    recordId: assignment.id,
                    operation: "DELETE",
                    changes: fullRowChanges(assignment, "DELETE"),
                    recordLabel: project.name,
                    officeId: project.office_id ?? null,
                });
            }

            // Then delete the project
            markSelfEdit("projects", project.id);
            const { error: deleteError } = await db.projects.delete(project.id);
            if (deleteError) {
                setError("Failed to delete project");
                setSubmitting(false);
                return;
            }
            recordAudit({
                table: "projects",
                recordId: project.id,
                operation: "DELETE",
                changes: fullRowChanges(project, "DELETE"),
                recordLabel: project.name,
                officeId: project.office_id ?? null,
            });

            onConfirm();
            close();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to delete project");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div>
            {error && (
                <div className="error-banner">
                    <span className="error-banner__message">{error}</span>
                </div>
            )}
            <p>
                Are you sure you want to permanently delete "{project.name}"? This cannot be undone.
            </p>
            <div className="modal-actions">
                <button
                    type="button"
                    className="secondary-button"
                    onClick={close}
                    disabled={submitting}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="danger-button"
                    onClick={handleConfirm}
                    disabled={submitting}
                >
                    {submitting ? "Deleting..." : "Delete Permanently"}
                </button>
            </div>
        </div>
    );
}
